/*
PW Menu Item

Generic li/a type menu item. Requires PW ReactBase wrapper to function properly
Suitable wrappers
  - Tabs
  - DropdownMenu

Options (props)
- active [bool]: is this the active tab
- disabled [bool]: is tab disabled
- onClick [fn]: function to call on click (overrides any set href) - will be passed any args, plus the event
- args [array|*]: argument(s) to be passed to onClick
- href [string]: pass to <a>. Standard behavior unless onClicked is defined
- headingText [string]: (only applies within DropdownMenu) if this has a value, a heading pseudo-element will be inserted above this MenuItem.
- dividerAfter [bool]: (only applies within DropdownMenu) insert a "divider" bar after this menuItem

example configuration:
  <ReactBase.Tab headingText="Heading Foo" onClicked={ myFuncRef }>Foo</ReactBase.Tab>

Some code borrowed/inspired by https://github.com/react-bootstrap/react-bootstrap/blob/master/src/NavItem.jsx

*/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import cx from 'classnames'

const propTypes = {
  args: T.any,
  active: T.bool,
  allowActiveClick: T.bool,
  className: T.string,
  disabled: T.bool,
  href: T.string,
  headingText: T.string,
  dividerAfter: T.bool,
  target: T.string, // passthrough for <a> attr
  indicatorClassName: T.string, //used by parent Tabs
  ignoreOnClick: T.bool
}

class MenuItem extends React.Component {
  static displayName = 'MenuItem'

  static propTypes = propTypes

  static defaultProps = {
    active: false,
    allowActiveClick: false,
    className: '',
    disabled: false,
    ignoreOnClick: false
  }

  onClick = e => {
    const { props } = this

    if (!props.ignoreOnClick && props.onClick) {
      e.preventDefault()
      if (!props.disabled && (props.allowActiveClick || !props.active)) {
        //only trigger if not disabled and not active
        const args = _.flatten([props.args, e])
        props.onClick(...args)
      }
    }
    else if (!props.href || props.disabled) {
      e.preventDefault()
    }
  }

  onClickActive = e => {
    const { props } = this
    e.preventDefault()
    if (props.onClick) {
      const args = _.flatten([props.args, e])
      props.onClick(...args)
    }
  }

  render() {
    const props = this.props
    const liProps = _.omit(props, _.keys(propTypes))

    const classes = cx(props.className, {
      pw_menu_item: true,
      active: props.active,
      disabled: props.disabled,
      hasHeading: props.headingText != null,
      hasDivider: props.dividerAfter
    })
    const ariaProps = {
      role: props.role || 'menuitem',
      [/check|radio/.test(props.role) ? 'aria-checked'
        : props.role === 'tab' ? 'aria-selected'
        : 'aria-current']: props.active,
      'aria-disabled': props.disabled
    }
    const anchorProps = {
      href: props.href || '#',
      onClick: this.onClick,
      target: props.target,
    }
    if (anchorProps.target === '_blank') {
      // See https://medium.com/@jitbit/target-blank-the-most-underestimated-vulnerability-ever-96e328301f4c#.x7e0gici1
      anchorProps.rel = "noopener noreferrer" 
      anchorProps['data-tooltip'] = "Opens in a new browser tab"
    }

    return (
      <li
        {...liProps}
        className={classes}
        data-headingtext={props.headingText}
        data-testid={props['data-testid']}
        onClick={null}
        title={props.title}
      >
        <a
          {...ariaProps}
          {...anchorProps}
        >
          {props.children}
        </a>
      </li>
    )
  }
}

export default MenuItem
